// src/services/storageService.js

import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './firebase';

/**
 * Uploads a file to Firebase Storage.
 * @param {File} file - The file to upload.
 * @param {string} path - The storage path (e.g., 'churches/churchId/logos/logo.png').
 * @returns {Promise<string>} The download URL of the uploaded file.
 */
export const uploadFile = async (file, path) => {
  const storageRef = ref(storage, path);
  const snapshot = await uploadBytes(storageRef, file);
  return getDownloadURL(snapshot.ref);
};

/**
 * Deletes a file from Firebase Storage using its download URL.
 * @param {string} fileUrl - The download URL of the file to delete.
 * @returns {Promise<void>}
 */
export const deleteFileByUrl = async (fileUrl) => {
  if (!fileUrl) return; // Do nothing if URL is empty or null

  try {
    const fileRef = ref(storage, fileUrl); // Firebase Storage can parse the ref from a download URL
    await deleteObject(fileRef);
    console.log("File deleted successfully from storage:", fileUrl);
  } catch (error) {
    if (error.code === 'storage/object-not-found') {
      console.warn("Attempted to delete a file that does not exist:", fileUrl);
    } else {
      console.error("Error deleting file from storage:", error);
      throw error; // Re-throw for upstream error handling
    }
  }
};

/**
 * Resizes and compresses an image file before uploading.
 * @param {File} file - The image file to resize.
 * @param {number} maxWidth - Maximum width for the resized image.
 * @param {number} maxHeight - Maximum height for the resized image.
 * @param {number} quality - JPEG compression quality (0.0 to 1.0).
 * @returns {Promise<File>} A Promise that resolves with the resized and compressed File object.
 */
export const resizeImage = (file, maxWidth, maxHeight, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions to fit within maxWidth/maxHeight while maintaining aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert canvas to Blob with specified quality (JPEG for smaller size)
        canvas.toBlob((blob) => {
          if (blob) {
            // Create a new File object from the blob, preserving the original file name but with jpeg type
            resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
          } else {
            reject(new Error('Canvas toBlob failed'));
          }
        }, 'image/jpeg', quality); // Force JPEG for better compression
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};